/**
 * Staged attachment — a File the user has queued up via the omnibar's
 * attachment buttons (image, camera, file, voice) but hasn't sent yet.
 *
 * The omnibar buffers attachments locally instead of uploading them on
 * the spot so the user can preview what they've picked, remove items
 * they didn't mean to attach, and combine attachments with a text
 * caption before committing the capture. On send, every staged
 * attachment becomes its own block via `blockStore.add({ files:
 * [file], ... })` — same path drag-drop and clipboard paste use today,
 * so all three input methods share the optimistic-placeholder + ingest
 * pipeline without any new server work.
 *
 * Discriminator (`type`) follows the project-wide convention (`type`,
 * not `kind`; see `.cursor/rules/discriminator-type.mdc`).
 */
/**
 * Defence-in-depth cap on how many attachments the user can stage at
 * once. `blockStore.add` and the server-side ingest pipeline both
 * enforce their own ceilings, but capping at the staging boundary
 * keeps the chip row from growing unboundedly tall on a pathological
 * multi-select (user picks 200 photos from their library) and gives
 * the user immediate visual feedback that the cap was hit instead of
 * a silent server-side rejection later.
 */
export const MAX_STAGED_ATTACHMENTS = 10;
/**
 * Generate a unique-enough id for a staged attachment. We don't need
 * crypto-grade uniqueness here — the id is only used as a React key
 * and as the handle the remove button passes back to the store. A
 * timestamp + random suffix collides only if two stages happen in
 * the same millisecond AND the random suffix collides; vanishingly
 * unlikely for human interaction speeds, and even if it happened the
 * worst case is React re-using a DOM node for the wrong chip until
 * the next state update.
 */
export function newStagedId() {
    return `att_${Date.now().toString(36)}_${Math.random().toString(36).slice(2, 8)}`;
}
/**
 * Build a `StagedImage` from a `File`, including the object URL for
 * preview. Kicks off async image decoding to populate the dimensions
 * once available — fires the supplied `onDimensions` callback so the
 * parent store can `set` the dimensions on the staged record. The
 * callback may fire after the staged record has already been removed;
 * that's fine, the parent should noop in that case.
 */
export function stageImage(file, onDimensions) {
    const previewUrl = URL.createObjectURL(file);
    if (onDimensions !== undefined) {
        // Image() is fast and doesn't block — the decode happens off the
        // main thread for the most part, and the load event fires once
        // the dimensions are known. We don't bother to display the full
        // image; this is purely a dimensions probe.
        const img = new Image();
        img.onload = () => {
            onDimensions({ width: img.naturalWidth, height: img.naturalHeight });
        };
        img.src = previewUrl;
    }
    return {
        id: newStagedId(),
        type: "image",
        file,
        previewUrl,
    };
}
export function stageFile(file) {
    return {
        id: newStagedId(),
        type: "file",
        file,
    };
}
export function stageVoice(file, peaks, durationSeconds) {
    return {
        id: newStagedId(),
        type: "voice",
        file,
        durationSeconds,
        peaks,
    };
}
/**
 * Release any browser-side resources owned by a staged attachment.
 * Today that's just the object URL on image stages; the helper is
 * type-aware so callers don't have to remember which variants need
 * cleanup.
 *
 * Safe to call repeatedly — `URL.revokeObjectURL` ignores URLs that
 * have already been revoked. Callers should run this on remove and
 * on send (after the file has been handed to `blockStore.add`).
 */
export function disposeStagedAttachment(attachment) {
    if (attachment.type === "image") {
        URL.revokeObjectURL(attachment.previewUrl);
    }
}
